import { useTemplateWithFields } from "@/hooks/useTemplateWithFields";
import { supabase } from "@/integrations/supabase/client";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

interface TemplateFormData {
  name: string;
}

export const useTemplateUpdateMutation = () => {
  const { template, invalidateQuery } = useTemplateWithFields();

  return useMutation({
    mutationFn: async (data: Partial<TemplateFormData>) => {
      if (!template) throw new Error("Template not loaded");
      const { error } = await supabase.from("templates").update(data).eq("id", template.id);
      if (error) throw error;
    },
    onError: (error) => {
      console.error("Error updating template:", error);
      toast.error("Failed to save changes");
    },
    onSuccess: () => {
      invalidateQuery();
      toast.success("Changes saved successfully");
    },
  });
};

export const useSearchTermsUpdateMutation = () => {
  const { template, setQueryData, invalidateQuery } = useTemplateWithFields();

  return useMutation({
    mutationFn: async (terms: string[]) => {
      if (!template) throw new Error("No template");

      const { error: deleteError } = await supabase
        .from("template_search_terms")
        .delete()
        .eq("template_id", template.id);

      if (deleteError) throw deleteError;

      if (terms.length > 0) {
        const { error: insertError } = await supabase.from("template_search_terms").insert(
          terms.map((term) => ({
            template_id: template.id,
            term,
          })),
        );

        if (insertError) throw insertError;
      }
    },
    onError: (error) => {
      console.error("Error updating search terms:", error);
      toast.error("Failed to save search terms");
    },
    onSuccess: () => {
      invalidateQuery();
    },
  });
};
